import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '../Icon';
import { Loader } from '../Loader';
import { StepsV2, PLAN_STEP } from '../StepsV2';
import { SubscriptionPlanSelectorV10 } from '../SubscriptionPlanSelectorV10';
import { CheckoutV4 } from '../CheckoutV4';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useIntersectionTracking } from '../../shared/use-intersection-tracking';
import { scrollToPlans } from '../../shared/scroll-helpers';
import { createSubscriptionForm } from '../../../lib/use-cases/create-subscription';
import { handleCreateSubscriptionError } from '../../shared/error-helpers';
import { resolveFirstStepUrl } from '../../../lib/shared/upsell-flow';
import { getCurrentPrelander } from '../../../lib/shared/prelander-helpers';
import { getMetadata } from '../../../lib/shared/ad-helpers';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { getPlanDiscountPercent } from '../../shared/plan-helpers';
import {
    MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN,
    MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN,
    MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN
} from '../../../lib/shared/subscription-plans';
import styles from './SaleV34.css';

const discountedPlans = [MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN, MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN];
const popularPlans = [MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN];

function SaleV34({
    data,
    initialPrelander,
    selectSubscription,
    selectedSubscriptionData,
    merchantData,
    setMerchantData,
    legalLinks,
    legalEntity
}) {
    const { plans, isLoading } = useSubscriptionPlans([]);
    const [isPending, setIsPending] = useState(false);
    const [activePlanData, setActivePlanData] = useState({});
    const firstUpsellUrl = resolveFirstStepUrl(getCurrentPrelander(), merchantData.subscriptionId);

    useIntersectionTracking('.fp-block');

    const handleClose = () => {
        selectSubscription({});
        setMerchantData({});
        scrollToPlans();
    };

    useEffect(() => {
        const handleNavigation = e => {
            if (e.navigationType === 'traverse' && window.history.state?.checkoutVisible) {
                window.history.replaceState({ checkoutVisible: false }, '');
                handleClose();
            }
        };

        logEvent(EVENT_TYPES.SALE_PAGE_VISITED);

        window.navigation.addEventListener('navigate', handleNavigation);
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        return () => {
            window.navigation.removeEventListener('navigate', handleNavigation);
        };
    }, []);

    const getPlans = () => {
        return discountedPlans
            .map(plan => plans.find(item => item.plan === plan))
            .filter(Boolean)
            .map(plan => ({ ...plan, isPopular: popularPlans.includes(plan.plan) }));
    };

    useEffect(() => {
        const newPlans = getPlans();
        const defaultPlan = newPlans.find(plan => plan.isPopular) ?? newPlans[0];

        if (defaultPlan) {
            setActivePlanData(defaultPlan);
        }
    }, [plans]);

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                metadata: getMetadata(window.location.search, initialPrelander, data.userResponses)
            });

            window.history.pushState({ checkoutVisible: true }, '');
            setMerchantData(newMerchantData);

            if (newMerchantData?.fbEventId) {
                logEvent(EVENT_TYPES.SALE_CHECKOUT_INITIATE, { eventId: newMerchantData.fbEventId });
            }
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                id: subscriptionId,
                metadata: getMetadata(window.location.search, initialPrelander, data.userResponses)
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const handleSubscribe = subscriptionData => {
        logEvent(EVENT_TYPES.PLAN_SELECTED, { plan: subscriptionData.plan });
        setMerchantData({});
        selectSubscription(subscriptionData);
        submitSubscription(subscriptionData);
    };

    if (merchantData.merchant && selectedSubscriptionData.plan) {
        return (
            <CheckoutV4
                merchantData={merchantData}
                subscriptionData={selectedSubscriptionData}
                promoType={data.promoType}
                onClose={handleClose}
                tryAgain={tryAgain}
                isPending={isPending}
                successUrl={firstUpsellUrl}
                termsOfUseLink={legalLinks.termsOfUse}
                defaultDiscountValue={getPlanDiscountPercent(selectedSubscriptionData)}
                legalEntity={legalEntity}
                isWalletEnabled
            />
        );
    }

    return (
        <div className={styles.root}>
            <StepsV2 currentStepId={PLAN_STEP} />
            <div className={styles.coupon}>
                <Icon name='discountTag' className={styles.couponIcon} />
                <p>
                    {'The coupon code '}
                    <strong>welcome2026</strong>
                    {' has been applied. '}
                    <strong>Your discount of up to 42% is now active.</strong>
                    {' Enjoy your savings!'}
                </p>
            </div>
            <div className={styles.content}>
                <div id='subscription-start-block' className={styles.subscriptionStartBlock} />
                <h1 className={styles.title}>Choose your Boundly plan</h1>
                <p className={styles.description}>Every plan gives you access to personalized questions about your whole life. Answer by voice or text at your own pace, and Boundly will shape your memories into a polished digital book.</p>
                <div id={EVENT_TYPES.FIRST_PLAN_VIEWED} className={`${styles.plansBlock} fp-block`}>
                    {isLoading
                        ? <Loader className={styles.loader} variant='secondary' />
                        : (
                            <SubscriptionPlanSelectorV10
                                onSubmit={handleSubscribe}
                                selectedSubscriptionData={activePlanData}
                                selectSubscription={setActivePlanData}
                                isPending={isPending}
                                plans={getPlans()}
                            />
                        )
                    }
                </div>
            </div>
        </div>
    );
}

SaleV34.propTypes = {
    data: PropTypes.object.isRequired,
    initialPrelander: PropTypes.string.isRequired,
    selectSubscription: PropTypes.func.isRequired,
    selectedSubscriptionData: PropTypes.object.isRequired,
    merchantData: PropTypes.object.isRequired,
    setMerchantData: PropTypes.func.isRequired,
    legalLinks: PropTypes.object.isRequired,
    legalEntity: PropTypes.object.isRequired
};

export {
    SaleV34
};
